import MonacoEditor, {
  type BeforeMount,
  type OnMount,
} from "@monaco-editor/react";
import type { editor } from "monaco-editor";
import { getLanguageFromFilename } from "../../utils/file-types";
import { initializeMonaco, setMonacoTheme } from "../../utils/monaco-setup";

interface ModelEditorProps {
  filename: string;
  content: string;
  isDark?: boolean;
  className?: string;
}

export function ModelEditor({
  filename,
  content,
  isDark = false,
  className = "",
}: ModelEditorProps) {
  const language = getLanguageFromFilename(filename) || "plaintext";

  const options: editor.IStandaloneEditorConstructionOptions = {
    readOnly: true,
    minimap: { enabled: false },
    codeLens: false,
    fixedOverflowWidgets: true,
    rulers: [],
    renderLineHighlight: "none",
    folding: false,
    guides: { indentation: false },
    wordBasedSuggestions: "off",
    occurrencesHighlight: "off",
    fontFamily: "IBM Plex Mono, monospace",
    stickyScroll: {
      enabled: false,
    },
    fontSize: 13,
    theme: isDark ? "gh-dark" : "gh",
    wordWrap: "on",
    scrollBeyondLastLine: false,
    automaticLayout: true,
    lineNumbers: "on",
    glyphMargin: false,
    lineDecorationsWidth: 10, // Add some space for decorations
    lineNumbersMinChars: 4, // Increase minimum characters for line numbers
    renderWhitespace: "none",
    padding: {
      bottom: 8, // Add bottom padding for better spacing
      top: 8, // Add top padding for better spacing
    },
  };

  const handleEditorWillMount: BeforeMount = (monaco) => {
    initializeMonaco(monaco);
  };

  const handleEditorDidMount: OnMount = (editor, monaco) => {
    if (!editor || !monaco) {
      return;
    }

    // Set the theme after editor is mounted
    setMonacoTheme(monaco, isDark);

    // Focus the editor for better UX
    editor.focus();
  };

  return (
    <div className={`h-full min-h-[400px] ${className}`}>
      <MonacoEditor
        height="100%"
        language={language}
        value={content}
        options={options}
        beforeMount={handleEditorWillMount}
        onMount={handleEditorDidMount}
        loading={
          <div className="flex items-center justify-center h-[400px]">
            <div className="text-r8-gray-11">Loading editor...</div>
          </div>
        }
      />
    </div>
  );
}
